import { IntlShape } from 'react-intl';
import { ControllerMessages } from '../../intl/ControllerMessages';
import { GenericMessages } from '../../intl/GenericMessages';
import { Firmware } from '../../models/firmware';

export const getFirmwareReleaseTypeIntlMessage = (
  releaseType?: string,
):
  | {
      id: string;
      defaultMessage: string;
      description: string;
    }
  | undefined => {
  switch (releaseType) {
    case 'custom':
      return ControllerMessages.firmwareVersionCustom;
    case 'beta':
      return ControllerMessages.firmwareVersionBeta;
    case 'dev':
      return ControllerMessages.firmwareVersionDev;
    case 'field test':
      return ControllerMessages.firmwareVersionFieldTest;
    case 'paid':
      return ControllerMessages.firmwareVersionPaid;
    case 'global':
    default:
      return undefined;
  }
};

export const getFirmwareDisplayFromFirmware = (intl: IntlShape, firmware?: Firmware): string => {
  if (!firmware) {
    return intl.formatMessage(GenericMessages.unknownPlaceholder);
  }

  return getFirmwareDisplayFromFirmwareInfo(intl, firmware.version, firmware.releaseType, firmware.displayName);
};

export const getFirmwareDisplayFromFirmwareInfo = (
  intl: any,
  version?: string,
  releaseType?: string,
  displayName?: string,
): string => {
  if (!version) {
    return intl.formatMessage(GenericMessages.unknownPlaceholder);
  }

  const releaseTypeMessage = getFirmwareReleaseTypeIntlMessage(releaseType);
  if (!releaseTypeMessage || !intl) {
    return `v${version} ${displayName ?? ''}`.trim();
  }

  return `v${version} ${intl.formatMessage(releaseTypeMessage)} ${displayName ?? ''}`.trim();
};

/**
 * Calculates if a semantic version value is less than another semantic version.
 *
 * @param versionA The first version for comparison
 * @param versionB The second version of comparison
 *
 * @returns 1 if versionA is bigger than versionB, -1 if versionA is smaller than versionB, or 0 if they are equal
 */
export const compareFirmwareVersions = (versionA?: string, versionB?: string) => {
  // Handles simple comparison case and case where both version A and B are undefined.
  if (versionA === versionB) {
    return 0;
  }

  // If one or the other is undefined, return code respecting the non-falsy value as greater than the falsy value.
  if (!versionA) {
    return -1;
  }
  if (!versionB) {
    return 1;
  }

  const vAParts = versionA.split('.').map(n => Number(n));
  const vBParts = versionB.split('.').map(n => Number(n));

  // If versions are not semantic, handle with a string comparison sort.
  if (vAParts.length !== 3 || vBParts.length !== 3) {
    return versionA.localeCompare(versionB, undefined, { numeric: true, sensitivity: 'base' });
  }

  // Guaranteed to be semantic versions with 0, 1, and 2 indexed entries.
  for (let i = 0; i < 3; i++) {
    const a = vAParts[i];
    const b = vBParts[i];
    if (a === b) continue;
    if (a < b) return -1;
    if (a > b) return 1;
  }

  // If identical, return 0
  return 0;
};
